import React from "react";
import { useNavigate } from "react-router-dom";
import heroTravelFamily from "@/assets/hero-travel-family.jpg";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const { isRTL } = useLanguage();

  const scrollToHowItWorks = () => {
    const section = document.getElementById("how-it-works");
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <section className="relative overflow-hidden min-h-[90vh] flex items-center">
        {/* Background image */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroTravelFamily})`,
          }}
        />

        {/* Dark overlay */}
        <div
          className="absolute inset-0 pointer-events-none bg-black/45"
          aria-hidden="true"
        />

        {/* Foreground content */}
        <div className="relative z-10 w-full pt-24 md:pt-32 pb-16 md:pb-24">
          <div
            className={`max-w-4xl mx-auto px-4 md:px-6 md:max-w-6xl grid md:grid-cols-2 gap-10 md:gap-8 items-center ${isRTL ? "text-right md:pr-12" : "text-left md:pl-12"}`}
          >
            <div>
              <h1
                className="text-4xl sm:text-5xl md:text-6xl font-heading font-semibold leading-[1.1] tracking-tight text-white fade-in-slow"
                style={
                  isRTL
                    ? { fontFamily: "'Segoe UI', Tahoma, Arial, sans-serif" }
                    : undefined
                }
              >
                The Smarter Way to Pay for Life's Big Moments
              </h1>

              <p
                className="mt-6 max-w-xl text-lg md:text-xl text-white/80 leading-relaxed font-body fade-in-slow"
                style={{
                  animationDelay: "0.1s",
                  ...(isRTL
                    ? { fontFamily: "'Segoe UI', Tahoma, Arial, sans-serif" }
                    : {}),
                }}
              >
                Compare Tabby, Tamara, Cashew and more — side by side. Book your
                trip and pay in monthly instalments. 0% interest if eligible. Fast approval.
              </p>

              {/* CTA buttons */}
              <div
                className={`mt-10 md:mt-12 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 fade-in-slow ${isRTL ? "sm:justify-end" : "sm:justify-start"}`}
                style={{ animationDelay: "0.2s" }}
              >
                <Button
                  size="lg"
                  className="font-medium w-full sm:w-auto"
                  onClick={() => navigate("/travel")}
                >
                  Search Travel Now
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-2 border-white text-white bg-white/[0.15] hover:bg-white/[0.28] hover:text-white font-medium w-full sm:w-auto"
                  onClick={scrollToHowItWorks}
                >
                  How It Works
                </Button>
              </div>

              {/* Trust badges */}
              <div
                className={`mt-6 inline-flex flex-wrap items-center gap-x-4 gap-y-2 text-xs sm:text-sm text-white/90 fade-in-slow rounded-full bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2.5 ${isRTL ? "justify-end" : "justify-start"}`}
                style={{ animationDelay: "0.3s" }}
              >
                <span className="inline-flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  256-bit encrypted
                </span>
                <span className="w-px h-3.5 bg-white/20" />
                <span className="inline-flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" />
                  </svg>
                  UAE Central Bank regulated
                </span>
                <span className="w-px h-3.5 bg-white/20" />
                <span className="inline-flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  0% interest BNPL
                </span>
              </div>
            </div>

            {/* Product preview mockup — desktop only */}
            <div
              className="hidden md:block fade-in-slow"
              style={{ animationDelay: "0.35s" }}
              aria-hidden="true"
            >
              <div className="bg-white/[0.82] backdrop-blur-[8px] rounded-2xl shadow-2xl ring-1 ring-black/5 p-5 text-left">
                <div className="flex items-center justify-between mb-4 px-1">
                  <p className="text-xs font-medium text-foreground/60 uppercase tracking-wide">
                    Compare payment plans
                  </p>
                  <span className="text-[10px] text-foreground/40 whitespace-nowrap">AED <span className="font-mono">4,700</span></span>
                </div>

                <div className="space-y-3">
                  {[
                    {
                      name: "Tabby",
                      payments: "4 payments of",
                      amount: "AED 1,175",
                      tag: "Instant approval",
                    },
                    {
                      name: "Cashew",
                      payments: "6 payments of",
                      amount: "AED 783",
                      tag: "Lowest monthly",
                    },
                    {
                      name: "Tamara",
                      payments: "4 payments of",
                      amount: "AED 1,175",
                      tag: "Instant approval",
                    },
                  ].map((p) => (
                    <div
                      key={p.name}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-background/50 px-3.5 py-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground">
                          {p.name}
                        </p>
                        <p className="text-xs text-foreground/60 mt-0.5">
                          {p.payments}{" "}
                          <span className="font-mono text-foreground/80">
                            {p.amount}
                          </span>
                          /mo
                        </p>
                        <p className="text-[11px] text-foreground/50 mt-0.5">
                          0% interest · {p.tag}
                        </p>
                      </div>
                      <button
                        type="button"
                        tabIndex={-1}
                        className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground"
                      >
                        Select
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </>
  );
};

export default HeroSection;
